import { useCallback, useMemo, useState } from 'react'

import { Box, Button, Flex, Group, Modal, Switch, Text, Title } from '@mantine/core'
import { Tooltip } from '@mantine/core'
import { useDisclosure, useMediaQuery } from '@mantine/hooks'
import { IconUserPlus } from '@tabler/icons-react'
import { useTranslate } from 'react-admin'
import { useNavigate } from 'react-router-dom'
import { UserApiDTO, UserApiDTOScopes } from '../../codegen/generated/Api.schemas'
import { useGetApiUsers } from '../../codegen/generated/users'
import { LXNoData } from '../../components/LXNoData'
import { LXTable, LXTableColumn } from '../../components/LXTable/LXTable'
import { StylesHelper } from '../../helpers/Styles.helper'
import { UsersHelper } from '../../helpers/Users.helper'
import { useUserStore } from '../../stores/useUserStore'
import { CreateUser } from './CreateUser'

type LXUsersItemTableType = Pick<UserApiDTO, 'id'> & {
  email: JSX.Element
  roles: JSX.Element
  scopes: JSX.Element | JSX.Element[]
  isAdmin: boolean | undefined
}

export const UsersList = () => {
  const t = useTranslate()
  const navigate = useNavigate()
  const userStore = useUserStore()
  const tabletScreen = useMediaQuery(StylesHelper.TABLET)

  const [isModalOpened, { open: openModal, close: closeModal }] = useDisclosure(false)
  const [hasDisabledUsers, setHasDisabledUsers] = useState(true)

  const usersQuery = useGetApiUsers()

  const columns: LXTableColumn<LXUsersItemTableType>[] = useMemo(
    () => [
      { key: 'email', value: t('_generics.email'), type: 'component', size: 'sm' },
      { key: 'scopes', value: t('pages.users.scope'), type: 'component' },
      { key: 'roles', value: t('pages.users.roles'), type: 'component' },
      { key: 'isAdmin', value: t('pages.users.admin'), type: 'boolean' },
    ],
    [t],
  )

  const getScopes = useCallback(
    (_scopes?: UserApiDTOScopes) => {
      if (!_scopes?.cms[0] && !_scopes?.beneficiariesIds[0] && !_scopes?.youtubeChannelsIds[0]) {
        return <LXNoData size="sm" my={0} ta="left" />
      }

      if (
        _scopes?.cms[0] === '*' &&
        _scopes?.beneficiariesIds[0] === '*' &&
        _scopes?.youtubeChannelsIds[0] === '*'
      ) {
        return (
          <Text size="sm" fs="italic">
            {t('_generics.all_one')}
          </Text>
        )
      }

      return (
        <Group wrap={tabletScreen ? 'nowrap' : undefined}>
          {Object.entries(_scopes ?? {}).map(([key, _sc]) => (
            <Flex justify="flex-start" align="center" gap={2} w="fit-content">
              <Text pt={3} size="sm">
                {_sc[0] === '*' ? (
                  t(`pages.users.allScopes.${key}`)
                ) : (
                  <Flex align="baseline">
                    {_sc.length} {t(`pages.users.scopeKeys.${key}`)}
                  </Flex>
                )}
              </Text>
            </Flex>
          ))}
        </Group>
      )
    },
    [t],
  )

  const users = useMemo(
    () =>
      (usersQuery.data?.body ?? [])
        .filter(u => (!hasDisabledUsers ? (u.roles ?? [])?.length > 0 : true))
        .map(u => ({
          id: u.id,
          email: (
            <Flex justify="flex-start" align="center" gap="xs">
              {UsersHelper.getDeactivatedUser(u, t('pages.users.userIsDisabled'))}
              <Text size="sm" fw={u?.roles?.length === 0 ? 'normal' : 'bold'}>
                {u.email}
              </Text>
            </Flex>
          ),
          scopes:
            (u.roles ?? [])?.some(r => r.id === UsersHelper.ADMIN_ROLE) === false ? (
              getScopes(u.scopes)
            ) : (
              <></>
            ),
          roles:
            u.roles && u.roles.length > 0 ? (
              <Text size="sm">
                {u.roles
                  .filter(r => r.id !== UsersHelper.ADMIN_ROLE)
                  .map(r => r.name)
                  .join(', ')}
              </Text>
            ) : (
              <LXNoData size="sm" my={0} ta="left" />
            ),
          isAdmin: (u.roles ?? [])?.some(r => r.id === UsersHelper.ADMIN_ROLE) ? true : undefined,
        })),
    [usersQuery.data?.body, hasDisabledUsers, t, getScopes],
  )

  const onCreateUser = useCallback(
    (userId: UserApiDTO['id']) => {
      closeModal()
      navigate(`/users-roles/users/${userId}/edit`)
    },
    [closeModal, navigate],
  )

  return (
    <Box py="sm">
      <Flex justify="flex-end">
        <Tooltip
          label={`⚠ ${t('pages.users.lackingPermission')} → ${t(
            'pages.users.permissionsList.users.edit',
          )}`}
          hidden={userStore.isAuthorised('users.edit')}
        >
          <span>
            <Button
              type="button"
              variant="subtle"
              size="xs"
              leftSection={<IconUserPlus size={14} />}
              onClick={openModal}
              disabled={!userStore.isAuthorised('users.edit')}
            >
              <Text size="xs" tt="uppercase">
                {t('pages.users.createUser')}
              </Text>
            </Button>
          </span>
        </Tooltip>
      </Flex>
      <Switch
        labelPosition="right"
        label={t('pages.users.disabledUsers')}
        onLabel={t('_generics.with')}
        offLabel={t('_generics.without')}
        checked={hasDisabledUsers}
        onChange={e => setHasDisabledUsers(e.currentTarget.checked)}
        w="fit-content"
      />
      <LXTable<LXUsersItemTableType>
        items={users}
        columns={columns}
        onRowClick={u => navigate(`/users-roles/users/${u.id}`)}
      />
      <Modal
        title={<Title size="h3">{t('pages.users.createUser')}</Title>}
        opened={isModalOpened}
        onClose={closeModal}
      >
        <CreateUser onCreate={onCreateUser} />
      </Modal>
    </Box>
  )
}
